/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import { loadInitiativeQuestions } from '../../../actions/blueprints';
import { useSearchThrottle } from './useSearchThrottle';
import { UtrTableEdit } from './UtrTableEdit';
import G17Client from '../../../services/G17Client';
import { RootState, useAppDispatch } from '../../../reducers';
import { UniversalTrackerPlain } from '../../../types/universalTracker';

interface UtrTableEditWrapperProps {
  usage: string;
  handleReload: () => void;
  currentQuestions: UniversalTrackerPlain[];
}

export const UtrTableEditWrapper = (props: UtrTableEditWrapperProps) => {
  const { handleReload, currentQuestions, usage } = props;

  const dispatch = useAppDispatch();
  const initiativeId = useSelector((state: RootState) => state.initiative.data?._id);
  const [questions, setSearchText] = useSearchThrottle();
  const [searchText, handleSearch] = React.useState('');
  const [errorMessage, setMessage] = React.useState('');
  const [isSaving, setSaving] = React.useState(false);

  useEffect(() => {
    if (initiativeId) {
      dispatch(loadInitiativeQuestions(initiativeId));
    }
  }, [dispatch, initiativeId]);

  if (!initiativeId) {
    return <>No reporting level selected</>;
  }

  const handleSearchUpdate = (search: string) => {
    handleSearch(search);
    setSearchText(search);
  };

  const isUTRInGroup = (utrId: string) => currentQuestions.some((utr) => utr._id === utrId);

  const processAction = async (action: Promise<any>) => {
    if (isSaving) {
      return;
    }
    setMessage('');
    setSaving(true);
    setMessage('');
    try {
      await action;
    } catch (e) {
      setMessage(e.message);
    }
    handleReload();
    setSaving(false);
  };

  const addToGroup = (utrId: string) =>
    processAction(
      G17Client.addUniversalTrackerToUsage({
        initiativeId,
        utrIds: [utrId],
        usage,
      }),
    );

  const removeFromGroup = (utrId: string) => {
    if (window.confirm('Are you sure you want to remove this metric from your profile dashboard highlights?')) {
      return processAction(
        G17Client.removeUniversalTrackerToUsage({
          initiativeId,
          utrIds: [utrId],
          usage,
        }),
      );
    } else {
      return Promise.resolve();
    }
  };

  return (
    <UtrTableEdit
      addToGroup={addToGroup}
      removeFromGroup={removeFromGroup}
      isUTRInGroup={isUTRInGroup}
      searchText={searchText}
      showHeader={false}
      currentQuestions={currentQuestions}
      questions={questions}
      handleSearch={handleSearchUpdate}
      isSaving={isSaving}
      errorMessage={errorMessage}
    />
  );
};
